// @ts-nocheck
import { message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  refreshTableCampaignsProducts,
  selectCampaignsProductsRefreshTable,
} from "../../../redux";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import {
  campaignProductsService,
  productsService,
} from "../../../shared/services";
import { fetchAllPaginated } from "../../../shared/utils/fetchAllPaginated";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";

interface Product {
  id: number;
  name: string;
  price: number;
  price_with_tax: number;
  discount_price: number;
  discount_price_whit_tax: number;
  status: string;
  slug: string;
  course: string;
  quantity?: number;
  type_media?: string;
  main_image?: string | null;
  products_media_id?: number;
}

export interface ISingleProductForCampaign {
  id: number;
  campaign_id: number;
  product_id: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: null;
  product: Product;
}

type ProductOption = {
  value: string;
  title: string;
  disabled?: boolean;
};

export const CAMPAIGN_PRODUCTS_MAX = 200;

function toProductOptions(
  data: Product[],
  existingProductIds: Set<number>
): ProductOption[] {
  return (data || []).map((item: Product) => {
    const already = existingProductIds.has(Number(item.id));
    return {
      value: item.id.toString(),
      title: already ? `${item.name} (ya en la campaña)` : item.name,
      disabled: already,
    };
  });
}

export const useCampaignsProducts = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRefreshTableCampaignsProducts = useSelector(
    selectCampaignsProductsRefreshTable
  );
  const { pathname } = useLocation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [tableProps, setTableProps] = useState<TableProperties>();
  const [selectedValue, setSelectedValue] = useState<string>();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [data_value, setDataValue] = useState<string>();
  const [existingProductIds, setExistingProductIds] = useState<Set<number>>(
    () => new Set()
  );
  const [rawProductOptions, setRawProductOptions] = useState<Product[]>([]);
  const [arrOptionsProducts, setArrOptionsProducts] = useState<
    ProductOption[]
  >([]);

  const changeSelectValue = (val: string) => {
    setSelectedValue(val);
  };

  const onInputKeyDown = (e: any) => {
    setDataValue(e);
  };

  const loadExistingProductIds = useCallback(async () => {
    const campaignId = Number(id);
    if (!Number.isFinite(campaignId) || campaignId <= 0) return;

    try {
      const rows = await fetchAllPaginated<{ product_id?: number }>(
        (query) => campaignProductsService.find({ query }),
        { campaign_id: campaignId }
      );
      setExistingProductIds(
        new Set(
          rows
            .map((row) => Number(row.product_id))
            .filter((productId) => Number.isFinite(productId) && productId > 0)
        )
      );
    } catch {
      // Si falla el listado, el API igual rechaza el duplicado.
    }
  }, [id]);

  useEffect(() => {
    setArrOptionsProducts(toProductOptions(rawProductOptions, existingProductIds));
  }, [rawProductOptions, existingProductIds]);

  useEffect(() => {
    if (data_value) {
      serviceSelector();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_value]);

  useEffect(() => {
    const campaignId = pathname.split("/")[2];
    if (campaignId === "new") return;
    if (isNaN(Number(campaignId))) {
      navigate(`/campaigns`);
      return;
    }
    void loadExistingProductIds();
    if (arrOptionsProducts.length === 0) {
      serviceSelector();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const serviceSelector = async () => {
    const query = {
      $limit: 20,
      status: "active",
      data_value: data_value,
    };
    await productsService.find({ query }).then(({ data }: Product[]) => {
      setRawProductOptions(data || []);
    });
  };

  const onSubmitCreateProductForCampaign = async (): Promise<boolean> => {
    if (isLoading) return false;
    setIsLoading(true);
    if (!selectedValue) {
      setIsLoading(false);
      message.error(
        `Debes de seleccionar un producto antes de añadirlo a la campaña`
      );
      return false;
    }

    if (existingProductIds.has(Number(selectedValue))) {
      setIsLoading(false);
      message.warning("Este producto ya está en la campaña");
      return false;
    }

    if (existingProductIds.size >= CAMPAIGN_PRODUCTS_MAX) {
      setIsLoading(false);
      message.warning(
        `La campaña admite máximo ${CAMPAIGN_PRODUCTS_MAX} productos`
      );
      return false;
    }

    try {
      const res = await campaignProductsService.create({
        product_id: Number(selectedValue),
        campaign_id: Number(id),
      });
      setIsLoading(false);
      if (res?.id) {
        message.success(
          `Se ha añadido con éxito el producto a la campaña con id:${res.id}`
        );
        setSelectedValue(undefined);
        dispatch(refreshTableCampaignsProducts(true));
        await loadExistingProductIds();
        return true;
      }
      message.error("No se pudo agregar el producto a la campaña");
      return false;
    } catch (err: any) {
      setIsLoading(false);
      message.error(
        err?.message || "No se pudo agregar el producto a la campaña"
      );
      return false;
    }
  };

  useEffect(() => {
    if (isRefreshTableCampaignsProducts) {
      tableProps?.refresh();
      void loadExistingProductIds();
      dispatch(refreshTableCampaignsProducts(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTableCampaignsProducts]);

  return {
    id,
    isLoading,
    isRefreshTableCampaignsProducts,
    tableProps,
    arrOptionsProducts,
    selectedValue,
    modalIsOpen,
    productCount: existingProductIds.size,
    maxProducts: CAMPAIGN_PRODUCTS_MAX,
    setTableProps,
    setModalIsOpen,
    onSubmitCreateProductForCampaign,
    changeSelectValue,
    onInputKeyDown,
  };
};
